import { cardWasCreatedOnDifferentBoard, isActionVisible } from "@/components/utils/actions";
import type { BoardType } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import type { Card } from "@/model/card";
import { isTeamLevelWorkitem } from "@/model/stickyType";

type IsStickyDeletableFn = (boardType: BoardType) => boolean;

/** The sticky is an origin on this board (not a mirror/copy from another board). */
export const isOrigin = (card: Card, board: Board) => !cardWasCreatedOnDifferentBoard(card, board);

/**
 * Can this sticky be permanently deleted (app + ALM)?
 * Origin on this board, and either non-ALM or the ALM config allows deletion.
 */
export const canDelete = (card: Card, board: Board, isStickyDeletable: IsStickyDeletableFn) =>
  isOrigin(card, board) &&
  (!card.almId || isStickyDeletable(board.type)) &&
  isActionVisible("delete", card, board);

/**
 * Can this sticky be unplanned on this board?
 * Only on team boards; two flavors:
 *   A) backlog-origin mirror (removing the mirror clears iteration on origin)
 *   B) team-level ALM-synced origin (returns to Work Item Selector)
 */
export const canUnplan = (card: Card, board: Board) =>
  board.type === "team" &&
  ((card.type.origin === "backlog" && !isOrigin(card, board)) || // flavor A
    (isTeamLevelWorkitem(card) && !!card.almId && isOrigin(card, board))); // flavor B

/**
 * A mirror can be removed from the current board; the origin is untouched.
 * Unplan takes precedence when applicable (backlog mirrors on team boards) —
 * semantically it's the same backend call, but labelled for clarity of intent.
 */
export const canRemove = (card: Card, board: Board) =>
  !isOrigin(card, board) && !canUnplan(card, board);

/** ALM config forbids deleting this sticky on this board. */
export const isDeleteBlockedByAlm = (
  card: Card,
  board: Board,
  isStickyDeletable: IsStickyDeletableFn,
) =>
  isOrigin(card, board) &&
  !!card.almId &&
  !isStickyDeletable(board.type) &&
  isActionVisible("delete", card, board);
